import { useMemo, useState } from 'react';
import type { Field } from '@formily/core';
import { useField, useFieldSchema } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { Button, Modal } from 'antd';

import PreviewFlowChart from './PreviewFlowChart';
import { getPath } from './transform';
import type { IDeriveConfig, IRoleVisual } from './deriveGraph';
import type { IComponentSchema } from '../../types';

type Obj = Record<string, unknown>;

/** CodeEditor config props arrive as JSON strings; parse them, tolerate objects. */
const parseMaybe = <T,>(value: unknown, fallback: T): T => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
};

/**
 * Read-only flow-chart PREVIEW. Generated from the whole document via
 * `deriveGraph` (config = `deriveConfig`); the user can only adjust main
 * connections (persisted to `relatedNode`) and node positions. MBO records
 * themselves are edited elsewhere (the resource Array-Tables).
 */
const FormFlowChart = observer(() => {
  const field = useField<Field>();
  const schema = useFieldSchema();
  // Config props: the sub-schemas (nodeCardSchema/panelSchema) are stringified by
  // escapeFormItemsSchemas and may not survive on a void field's componentProps —
  // so read from the field schema's x-component-props too (componentProps wins).
  const rawProps = (schema?.['x-component-props'] ?? {}) as Obj;
  const props = { ...rawProps, ...((field.componentProps ?? {}) as Obj) };

  const config = useMemo(
    () => parseMaybe<IDeriveConfig | undefined>(props.deriveConfig, undefined),
    [props.deriveConfig]
  );

  // Role visuals from nodeGroups (key → color/icon/label).
  const visuals = useMemo(() => {
    const groups = parseMaybe<
      Array<{ key: string; label?: string; color?: string; icon?: string }>
    >(props.nodeGroups, []);
    const m: Record<string, IRoleVisual> = {};
    for (const g of groups) m[g.key] = { label: g.label, color: g.color, icon: g.icon };
    return m;
  }, [props.nodeGroups]);

  const form = field.form;
  const opsPath = config?.operationsPath ?? 'manufacturingBusinessOperations';

  // Persisted relatedNode entry ids (captured once from the loaded document) →
  // soft-delete on removal.
  const [originalRelationIds] = useState<Set<string>>(() => {
    const relField = config?.relationField ?? 'relatedNode';
    const initial = getPath(form?.initialValues ?? {}, opsPath);
    const ids = new Set<string>();
    for (const item of (Array.isArray(initial) ? initial : []) as Obj[]) {
      const rel = getPath(item, relField);
      if (Array.isArray(rel))
        for (const e of rel) {
          const id = (e as Obj)?.id;
          if (id != null) ids.add(String(id));
        }
    }
    return ids;
  });

  const [open, setOpen] = useState(false);

  if (!config) {
    return <div style={{ color: '#64748b' }}>No derive config set.</div>;
  }

  // This is a VOID field — it reads/writes the MBO array via the form API (so it
  // doesn't collide with the Array-Table bound to the same path). A fresh
  // `$mainForm` snapshot on each render keeps deriveGraph reacting to changes.
  const mainForm: Obj = { ...((form?.values ?? {}) as Obj) };

  const chart = (h?: number | string) => (
    <PreviewFlowChart
      mainForm={mainForm}
      config={config}
      visuals={visuals}
      nodeCardSchema={parseMaybe<
        IComponentSchema<Record<string, unknown>> | undefined
      >(props.nodeCardSchema, undefined)}
      panelSchema={parseMaybe<
        IComponentSchema<Record<string, unknown>> | undefined
      >(props.panelSchema, undefined)}
      originalRelationIds={originalRelationIds}
      onChangeOperations={next => form?.setValuesIn(opsPath, next)}
      height={h ?? ((props.height as number) || undefined)}
      panelWidth={(props.panelWidth as number) || undefined}
    />
  );

  if (props.openInModal !== true) return chart();

  const align = (props.triggerButtonAlign as 'left' | 'center' | 'right') ?? 'left';
  const justify =
    align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: justify }}>
        <Button
          type={(props.triggerButtonType as 'primary' | 'default') ?? 'primary'}
          size={(props.triggerButtonSize as 'small' | 'middle' | 'large') ?? 'middle'}
          block={props.triggerButtonBlock === true}
          onClick={() => setOpen(true)}
        >
          {(props.triggerButtonText as string) ?? 'Open flow chart'}
        </Button>
      </div>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={(props.modalTitle as string) || 'Manufacturing flow chart'}
        footer={null}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0 }}
        styles={{
          content: {
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          },
          body: { flex: 1, minHeight: 0, overflow: 'hidden' },
        }}
      >
        {chart('100%')}
      </Modal>
    </>
  );
});

export default FormFlowChart;
